/**
 * spaceBoard - the document model of a space's Board, shared by the browser
 * editor and the server.
 *
 * The Board is one live page per space (a README for the engagement):
 * headings, text, task lists, tables, and figures. It is a Tiptap document
 * stored in a Yjs XML fragment, so people and the AI edit it at the same
 * time. Both sides build the editor schema from `spaceBoardExtensions`; the
 * browser adds collaboration, carets and its figure views on top, and the
 * server uses the same schema headless to apply the AI's edits.
 *
 * Figures stay Markdown: a chart is a ```chart code block holding an ECharts
 * option as JSON, exactly the fence a chat answer uses, so the AI writes the
 * same thing in both places and the browser renders it through the chat's
 * sanitiser.
 *
 * The AI edits the Board in Markdown. `applyBoardMarkdownEdit` turns an edit
 * into the smallest change to the shared fragment (`updateYFragment` diffs
 * the trees), so the carets of the people typing at the same time stay where
 * they are.
 *
 * @module spaceBoard
 */
import { getSchema, type AnyExtension, type JSONContent } from "@tiptap/core";
import { TableKit } from "@tiptap/extension-table";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { MarkdownManager } from "@tiptap/markdown";
import { Node as ProseMirrorNode, type Schema } from "@tiptap/pm/model";
import StarterKit from "@tiptap/starter-kit";
import { updateYFragment, yXmlFragmentToProsemirrorJSON } from "@tiptap/y-tiptap";
import * as Y from "yjs";

/** The Yjs fragment Tiptap's Collaboration extension binds by default. */
export const SPACE_BOARD_FRAGMENT = "default";

/** An edit larger than this is a data dump, not a board change. */
export const MAX_BOARD_EDIT_LENGTH = 200_000;

/** Fence languages the Board renders as a chart, as chat does. */
export const BOARD_CHART_LANGUAGES: ReadonlySet<string> = new Set(["chart", "echarts"]);

export interface SpaceBoardExtensionOptions {
  /**
   * Replaces StarterKit's code block, so the browser can render chart
   * fences as figures. It must keep the node name `codeBlock` and its
   * `language` attribute, or the two sides stop sharing a schema.
   */
  readonly codeBlock?: AnyExtension;
}

/**
 * The Board's extensions without anything that needs a browser. Undo is
 * left to the collaboration layer (per-person undo), so StarterKit's own
 * history is off.
 */
export function spaceBoardExtensions(options: SpaceBoardExtensionOptions = {}): AnyExtension[] {
  return [
    StarterKit.configure({
      undoRedo: false,
      ...(options.codeBlock ? { codeBlock: false } : {}),
    }),
    ...(options.codeBlock ? [options.codeBlock] : []),
    TaskList,
    TaskItem.configure({ nested: true }),
    TableKit,
  ];
}

interface BoardCodec {
  readonly schema: Schema;
  readonly markdown: MarkdownManager;
}

let codec: BoardCodec | null = null;

/** The headless schema and Markdown codec, built once. */
function boardCodec(): BoardCodec {
  if (codec === null) {
    const extensions = spaceBoardExtensions();
    codec = { schema: getSchema(extensions), markdown: new MarkdownManager({ extensions }) };
  }
  return codec;
}

export type BoardEditErrorReason = "too_large" | "invalid_chart";

export class BoardEditError extends Error {
  readonly reason: BoardEditErrorReason;
  constructor(reason: BoardEditErrorReason, message: string) {
    super(message);
    this.name = "BoardEditError";
    this.reason = reason;
  }
}

const EMPTY_DOC: JSONContent = { type: "doc", content: [{ type: "paragraph" }] };

/** The Board as ProseMirror JSON. */
export function readBoardJSON(doc: Y.Doc): JSONContent {
  const fragment = doc.getXmlFragment(SPACE_BOARD_FRAGMENT);
  if (fragment.length === 0) return EMPTY_DOC;
  return yXmlFragmentToProsemirrorJSON(fragment) as JSONContent;
}

/** The Board as Markdown, as the AI reads it. */
export function readBoardMarkdown(doc: Y.Doc): string {
  return boardCodec().markdown.serialize(readBoardJSON(doc)).trim();
}

/**
 * Parses Markdown into the Board's top-level blocks. `checkCharts` (the
 * default, for the AI's edits) refuses a chart fence that is not JSON; a
 * person's uploaded file keeps such a fence as the code it is.
 */
export function boardMarkdownToBlocks(
  markdown: string,
  options: { readonly checkCharts?: boolean } = {},
): JSONContent[] {
  if (markdown.length > MAX_BOARD_EDIT_LENGTH) {
    throw new BoardEditError(
      "too_large",
      `The edit is ${markdown.length} characters; the limit is ${MAX_BOARD_EDIT_LENGTH}.`,
    );
  }
  const parsed = boardCodec().markdown.parse(markdown) as JSONContent;
  const blocks = (parsed.content ?? []).filter((block) => !isEmptyParagraph(block));
  if (options.checkCharts !== false) assertChartsAreJson(blocks);
  return blocks;
}

const isEmptyParagraph = (block: JSONContent): boolean =>
  block.type === "paragraph" && (block.content === undefined || block.content.length === 0);

const blockText = (block: JSONContent): string =>
  block.text ?? (block.content ?? []).map(blockText).join("");

/**
 * A chart fence must hold one JSON object. The browser sanitises the option
 * before it draws anything; this only tells the AI early that it wrote
 * something no one will see.
 */
function assertChartsAreJson(blocks: ReadonlyArray<JSONContent>): void {
  for (const block of blocks) {
    if (block.type === "codeBlock") {
      const language = String(block.attrs?.language ?? "")
        .trim()
        .toLowerCase();
      if (BOARD_CHART_LANGUAGES.has(language)) {
        let value: unknown;
        try {
          value = JSON.parse(blockText(block));
        } catch {
          throw new BoardEditError(
            "invalid_chart",
            "A ```chart block must hold an ECharts option as one JSON object.",
          );
        }
        if (value === null || typeof value !== "object" || Array.isArray(value)) {
          throw new BoardEditError(
            "invalid_chart",
            "A ```chart block must hold an ECharts option as one JSON object.",
          );
        }
      }
    }
    if (block.content) assertChartsAreJson(block.content);
  }
}

export type BoardEdit =
  /** Replace the whole Board. */
  | { readonly kind: "replace"; readonly markdown: string }
  /** Add blocks at the end of the Board. */
  | { readonly kind: "append"; readonly markdown: string }
  /**
   * Edit the section under a heading (matched by its text, ignoring case and
   * surrounding space): `replace` its body, or add to the start (`prepend`)
   * or end (`append`) of its body. A section runs to the next heading of the
   * same or a higher level. When no heading matches, the section is added at
   * the end of the Board under that heading (level 2).
   */
  | {
      readonly kind: "section";
      readonly heading: string;
      readonly mode: "replace" | "append" | "prepend";
      readonly markdown: string;
    };

const normalizeHeading = (text: string): string =>
  text.trim().replace(/\s+/g, " ").replace(/^#+\s*/, "").toLowerCase();

/** The index range [start, end) of the section under `heading`, heading included. */
export function findBoardSection(
  blocks: ReadonlyArray<JSONContent>,
  heading: string,
): { readonly start: number; readonly end: number; readonly level: number } | null {
  const wanted = normalizeHeading(heading);
  const start = blocks.findIndex(
    (block) => block.type === "heading" && normalizeHeading(blockText(block)) === wanted,
  );
  if (start === -1) return null;
  const level = Number(blocks[start]!.attrs?.level ?? 1);
  let end = start + 1;
  while (end < blocks.length) {
    const block = blocks[end]!;
    if (block.type === "heading" && Number(block.attrs?.level ?? 1) <= level) break;
    end += 1;
  }
  return { start, end, level };
}

/** The Board's blocks after `edit`, without touching the document. */
export function planBoardEdit(
  current: ReadonlyArray<JSONContent>,
  edit: BoardEdit,
): JSONContent[] {
  const existing = current.filter((block) => !isEmptyParagraph(block));
  const incoming = boardMarkdownToBlocks(edit.markdown);
  switch (edit.kind) {
    case "replace":
      return incoming;
    case "append":
      return [...existing, ...incoming];
    case "section": {
      const section = findBoardSection(existing, edit.heading);
      if (section === null) {
        const heading: JSONContent = {
          type: "heading",
          attrs: { level: 2 },
          content: [{ type: "text", text: edit.heading.trim() }],
        };
        return [...existing, heading, ...incoming];
      }
      const before = existing.slice(0, section.start);
      const head = existing[section.start]!;
      const body = existing.slice(section.start + 1, section.end);
      const after = existing.slice(section.end);
      switch (edit.mode) {
        case "replace":
          return [...before, head, ...incoming, ...after];
        case "append":
          return [...before, head, ...body, ...incoming, ...after];
        case "prepend":
          return [...before, head, ...incoming, ...body, ...after];
      }
    }
  }
}

/**
 * Applies `edit` to the Board inside one Yjs transaction with `origin`, as
 * the smallest change to the shared fragment. Returns the Yjs update that
 * the change produced, for the caller to persist and broadcast; null when
 * the Board already reads that way.
 */
export function applyBoardMarkdownEdit(
  doc: Y.Doc,
  edit: BoardEdit,
  origin: unknown,
): Uint8Array | null {
  const { schema } = boardCodec();
  const current = readBoardJSON(doc).content ?? [];
  const blocks = planBoardEdit(current, edit);
  const next = ProseMirrorNode.fromJSON(schema, {
    type: "doc",
    content: blocks.length > 0 ? blocks : EMPTY_DOC.content,
  });
  // Rejects a tree the schema does not allow before it reaches anyone.
  next.check();
  const fragment = doc.getXmlFragment(SPACE_BOARD_FRAGMENT);
  // Yjs emits an update only for a transaction that changed something, and
  // a delete-only change leaves the state vector as it was, so listen.
  let update: Uint8Array | null = null;
  const capture = (bytes: Uint8Array, from: unknown) => {
    if (from === origin) update = bytes;
  };
  doc.on("update", capture);
  try {
    doc.transact(() => {
      updateYFragment(doc, fragment, next, { mapping: new Map(), isOMark: new Map() });
    }, origin);
  } finally {
    doc.off("update", capture);
  }
  return update;
}

/**
 * A Markdown file as the Yjs state of a new page (the Board's model): the
 * first state of a page created from an upload. Throws `BoardEditError`
 * (`too_large`) past {@link MAX_BOARD_EDIT_LENGTH} characters.
 */
export function boardStateFromMarkdown(markdown: string): Uint8Array {
  const { schema } = boardCodec();
  const blocks = boardMarkdownToBlocks(markdown.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n"), {
    checkCharts: false,
  });
  const node = ProseMirrorNode.fromJSON(schema, {
    type: "doc",
    content: blocks.length > 0 ? blocks : EMPTY_DOC.content,
  });
  node.check();
  const doc = new Y.Doc();
  try {
    const fragment = doc.getXmlFragment(SPACE_BOARD_FRAGMENT);
    doc.transact(() => {
      updateYFragment(doc, fragment, node, { mapping: new Map(), isOMark: new Map() });
    });
    return Y.encodeStateAsUpdate(doc);
  } finally {
    doc.destroy();
  }
}

/** A page's (or the Board's) Yjs state as Markdown, for a download. */
export function boardStateToMarkdown(state: Uint8Array): string {
  const doc = new Y.Doc();
  try {
    Y.applyUpdate(doc, state);
    const markdown = readBoardMarkdown(doc);
    return markdown.length > 0 ? `${markdown}\n` : "";
  } finally {
    doc.destroy();
  }
}
